import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ROLE_LABEL, ROLE_OPTIONS, formatEmployeeName, ORG_UNIT_LABEL, ORG_UNIT_OPTIONS, downloadAsBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Send, ArrowDownAZ, ArrowUpZA, Search, X, Trash2, AlertTriangle, Upload, FileSpreadsheet, Printer } from "lucide-react";
import EmployeeHistorySheet from "../components/EmployeeHistorySheet";
import BehalfVacationDialog from "../components/BehalfVacationDialog";
import { EmployeeEditDialog } from "../components/EmployeeEditDialog";
import { EmployeeImportDialog } from "../components/EmployeeImportDialog";
import { toast } from "sonner";

const DEFAULT_VACATION_DAYS = 30;
const MAX_DAYS_PER_YEAR = 365;

const EMPTY = { email: "", full_name: "", title: "", academic_title: "", katedra: "", organizacijska_jedinica_tip: "katedra", radno_mjesto: "", manager_email: "", role: "employee_nonteaching", ukupni_fond_dana: DEFAULT_VACATION_DAYS, iskoristeno_dana: 0, iskoristeno_dana_manual_override: false, custom_role_id: "", rfid_card_uid: "", work_schedule_id: "", forward_to_tajnik: false, attendance_override_enabled: false };

const getSurname = (name) => {
  const parts = (name || "").trim().split(" ");
  return parts[parts.length - 1] || "";
};

export default function Employees() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [behalfFor, setBehalfFor] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // asc | desc by surname
  const [search, setSearch] = useState("");
  const [filterKatedra, setFilterKatedra] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [filterOrgUnitTip, setFilterOrgUnitTip] = useState("all");
  const [showHistory, setShowHistory] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);  // { email, full_name }
  const [deleteConflict, setDeleteConflict] = useState(null);  // { message, active_count }
  const [deleting, setDeleting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const isFullAdmin = ["admin", "secretary"].includes(user.role);

  const [customRoles, setCustomRoles] = useState([]);
  const [workSchedules, setWorkSchedules] = useState([]);

  const load = useCallback(() => api.get("/users").then(r => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/custom-roles")
      .then(r => setCustomRoles(r.data || []))
      .catch(err => {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Custom roles fetch failed:", err?.message || err);
        }
      });
    api.get("/work-schedules")
      .then(r => setWorkSchedules(r.data || []))
      .catch(err => {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Work schedules fetch failed:", err?.message || err);
        }
      });
  }, []);

  const allKatedras = useMemo(() => {
    const set = new Set(users.map(u => u.katedra).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "hr"));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (filterKatedra !== "all" && u.katedra !== filterKatedra) return false;
      if (filterRole !== "all" && u.role !== filterRole) return false;
      if (filterOrgUnitTip !== "all" && (u.organizacijska_jedinica_tip || "katedra") !== filterOrgUnitTip) return false;
      if (q) {
        const hay = `${u.full_name} ${u.email} ${u.radno_mjesto || ""} ${u.katedra || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, filterKatedra, filterRole, filterOrgUnitTip]);

  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const sa = getSurname(a.full_name).toLowerCase();
      const sb = getSurname(b.full_name).toLowerCase();
      const cmp = sa.localeCompare(sb, "hr");
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredUsers, sortDir]);

  // Memoized list of users eligible to be a "manager_email" — used in the create/edit dialog Select.
  // Iter41b: prodekan dodan jer prodekani često sudjeluju u odobravanju zahtjeva.
  const managerCandidates = useMemo(
    () => users.filter(x => ["manager","prodekan","secretary","dean","admin"].includes(x.role)),
    [users]
  );

  const clearFilters = () => { setSearch(""); setFilterKatedra("all"); setFilterRole("all"); setFilterOrgUnitTip("all"); };
  const hasFilters = search || filterKatedra !== "all" || filterRole !== "all" || filterOrgUnitTip !== "all";

  const save = async () => {
    if (!form.full_name?.trim()) { toast.error("Ime i prezime je obavezno"); return; }
    if (!form.academic_title?.trim()) { toast.error("Akademsko zvanje je obavezno"); return; }
    try {
      // Pull out attendance fields — they go via a dedicated endpoint
      const { rfid_card_uid, work_schedule_id, ...rest } = form;
      const payload = {
        ...rest,
        academic_title: form.academic_title.trim(),
        ukupni_fond_dana: Number(form.ukupni_fond_dana) || 0,
        iskoristeno_dana: Number(form.iskoristeno_dana) || 0,
        iskoristeno_dana_manual_override: !!form.iskoristeno_dana_manual_override,
        // Iter45: prijenos iz prošle godine (null = nije postavljen, ne preuzima se)
        prev_year_fond: form.prev_year_fond === null || form.prev_year_fond === "" ? null : Number(form.prev_year_fond),
        prev_year_iskoristeno: form.prev_year_iskoristeno === null || form.prev_year_iskoristeno === "" ? null : Number(form.prev_year_iskoristeno),
      };
      let targetEmail;
      if (open === "new") {
        const res = await api.post("/users", payload);
        targetEmail = (res.data?.email || form.email).toLowerCase();
      } else {
        targetEmail = open;
        await api.put(`/users/${encodeURIComponent(targetEmail)}`, payload);
      }
      // Update attendance settings (RFID + schedule). Only fire if at least one is provided OR changed.
      if (rfid_card_uid !== undefined || work_schedule_id !== undefined) {
        try {
          await api.put(`/users/${encodeURIComponent(targetEmail)}/attendance`, {
            rfid_card_uid: (rfid_card_uid || "").trim() || null,
            work_schedule_id: work_schedule_id || null,
          });
        } catch (e) {
          // Attendance setting may fail (e.g., duplicate card UID) — surface but don't roll back user save
          toast.error(`Korisnik spremljen, ali postavke prisutnosti: ${e.response?.data?.detail || "greška"}`);
          setOpen(null); setForm(EMPTY); load();
          return;
        }
      }
      toast.success("Spremljeno"); setOpen(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Greška"); }
  };

  const doDelete = async (force = false) => {
    if (!deleteFor) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/users/${encodeURIComponent(deleteFor.email)}${force ? "?force=true" : ""}`);
      const cancelled = res.data?.cancelled_active_requests || 0;
      toast.success(
        cancelled > 0
          ? `Zaposlenik obrisan. Otkazano ${cancelled} aktivnih zahtjeva.`
          : "Zaposlenik obrisan."
      );
      setDeleteFor(null); setDeleteConflict(null); load();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "Greška";
      if (status === 409) {
        // Active requests — show force confirm step
        setDeleteConflict({ message: detail });
      } else {
        toast.error(detail);
        setDeleteFor(null);
      }
    } finally { setDeleting(false); }
  };

  const [importMode, setImportMode] = useState("idle"); // idle | preview | applied

  const _uploadImport = async (dryRun) => {
    if (!importFile) { toast.error("Odaberite datoteku"); return; }
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const url = `/users/csv-import${dryRun ? "?dry_run=true" : ""}`;
      const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" }});
      setImportResult(r.data);
      if (dryRun) {
        setImportMode("preview");
        if ((r.data.changes?.length || 0) === 0 && (r.data.errors?.length || 0) === 0) {
          toast.info("Nema promjena u datoteci u odnosu na bazu.");
        }
      } else {
        setImportMode("applied");
        const { added, updated, unchanged } = r.data;
        toast.success(`Uvoz dovršen — dodano: ${added}, ažurirano: ${updated}, bez promjena: ${unchanged}`);
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri uvozu");
    } finally { setImportBusy(false); }
  };

  const doPreviewImport = () => _uploadImport(true);
  const doApplyImport = () => _uploadImport(false);
  const resetImport = () => {
    setImportResult(null);
    setImportMode("idle");
    setImportFile(null);
  };
  const closeImportDialog = () => {
    setImportOpen(false);
    setTimeout(resetImport, 200);  // wait for dialog fade-out
  };

  return (
    <div className="space-y-4" data-testid="employees-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-wider">Administracija</p>
          <h1 className="serif text-3xl sm:text-4xl font-semibold">Zaposlenici</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isFullAdmin && (
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="open-import-btn">
              <Upload className="w-4 h-4 mr-1.5"/> Uvoz iz datoteke
            </Button>
          )}
          {isFullAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  toast.info("Generiram PDF naljepnice za sve zaposlenike…");
                  const r = await api.post("/admin/rfid-pi/stickers/bulk",
                    { all: true, layout: "grid" },
                    { responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "kif-naljepnice-svi.pdf";
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  toast.success("PDF preuzet — 10 naljepnica po stranici");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Greška");
                }
              }}
              data-testid="bulk-stickers-btn"
              title="PDF s naljepnicama za sve zaposlenike (10 po stranici)"
            >
              <Printer className="w-4 h-4 mr-1.5"/> Naljepnice (svi)
            </Button>
          )}
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/export/employees.xlsx`}
             data-testid="export-employees-btn">
            <Button variant="outline" size="sm">
              <FileSpreadsheet className="w-4 h-4 mr-1.5"/> Izvoz Excel
            </Button>
          </a>
          <Button onClick={() => { setForm(EMPTY); setOpen("new"); }} data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-1"/>Dodaj zaposlenika
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Pretraži po imenu, e-mailu, radnom mjestu, katedri…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="emp-search"
            />
          </div>
          <Select value={filterKatedra} onValueChange={setFilterKatedra}>
            <SelectTrigger className="w-[260px]" data-testid="emp-filter-katedra">
              <SelectValue placeholder="Sve katedre"/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sve katedre</SelectItem>
              {allKatedras.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-[230px]" data-testid="emp-filter-role">
              <SelectValue placeholder="Sve uloge"/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sve uloge</SelectItem>
              <SelectItem value="employee">Zaposlenik (legacy)</SelectItem>
              {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterOrgUnitTip} onValueChange={setFilterOrgUnitTip}>
            <SelectTrigger className="w-[210px]" data-testid="emp-filter-org-unit-tip">
              <SelectValue placeholder="Sve vrste jedinica"/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sve vrste jedinica</SelectItem>
              {ORG_UNIT_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="emp-clear-filters">
              <X className="w-4 h-4 mr-1"/>Očisti
            </Button>
          )}
          <div className="text-xs text-muted-foreground ml-auto" data-testid="emp-result-count">
            {sortedUsers.length} od {users.length} zaposlenika
          </div>
        </CardContent>
      </Card>

      <Card><CardContent className="p-0">
       {/* Desktop table view (>=640px) */}
       <div className="overflow-x-auto hidden sm:block">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-secondary/60"><tr>
            <th className="text-left p-3">
              <button
                onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
                className="inline-flex items-center gap-1 font-medium hover:text-primary"
                data-testid="sort-by-surname"
                title={`Sortiraj po prezimenu (${sortDir === "asc" ? "A→Z" : "Z→A"})`}
              >
                Prezime i ime {sortDir === "asc" ? <ArrowDownAZ className="w-3.5 h-3.5"/> : <ArrowUpZA className="w-3.5 h-3.5"/>}
              </button>
            </th><th className="text-left p-3">E-mail</th>
            <th className="text-left p-3">Radno mjesto / Katedra</th><th className="text-left p-3">Nadređeni</th>
            <th className="text-left p-3">Uloga</th><th className="text-left p-3">Fond / Iskorišt.</th><th className="text-right p-3">Akcije</th>
          </tr></thead>
          <tbody>{sortedUsers.map(u => (
            <tr key={u.email} className="border-t border-border" data-testid={`row-${u.email}`}>
              <td className="p-3">
                <button
                  className="text-left hover:text-primary"
                  onClick={() => setShowHistory(u)}
                  data-testid={`emp-name-${u.email}`}
                >
                  {formatEmployeeName(u)}
                </button>
              </td>
              <td className="p-3 text-xs text-muted-foreground">{u.email}</td>
              <td className="p-3 text-xs">
                <div>{u.radno_mjesto || u.katedra}</div>
                {u.organizacijska_jedinica_tip && u.organizacijska_jedinica_tip !== "katedra" && (
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {ORG_UNIT_LABEL[u.organizacijska_jedinica_tip] || u.organizacijska_jedinica_tip}
                  </div>
                )}
              </td>
              <td className="p-3 text-xs">{u.manager_email || "—"}</td>
              <td className="p-3 text-xs">
                {u.custom_role_name ? (
                  <div>
                    <div className="font-medium">{u.custom_role_name}</div>
                    <div className="text-[10px] text-muted-foreground">baza: {ROLE_LABEL[u.role] || u.role}</div>
                  </div>
                ) : (ROLE_LABEL[u.role] || u.role)}
              </td>
              <td className="p-3 text-xs">
                <div>{u.ukupni_fond_dana ?? DEFAULT_VACATION_DAYS} / <b>{u.iskoristeno_dana_effective ?? u.iskoristeno_dana ?? 0}</b></div>
                <div className="text-[10px] text-muted-foreground">{u.iskoristeno_dana_manual_override ? "početno + auto" : "auto"}</div>
              </td>
              <td className="p-3 text-right space-x-1">
                <Button size="icon" variant="ghost" onClick={() => { setForm({...EMPTY, ...u}); setOpen(u.email); }} data-testid={`edit-${u.email}`}><Pencil className="w-3 h-3"/></Button>
                <Button size="icon" variant="ghost" onClick={() => setBehalfFor(u)} title="Predaj zahtjev u ime" data-testid={`behalf-${u.email}`}><Send className="w-3 h-3"/></Button>
                {isFullAdmin && u.email !== user.email && (
                  <Button size="icon" variant="ghost"
                    onClick={() => { setDeleteFor({ email: u.email, full_name: u.full_name }); setDeleteConflict(null); }}
                    title="Obriši zaposlenika"
                    data-testid={`delete-${u.email}`}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3 h-3"/>
                  </Button>
                )}
              </td>
            </tr>
          ))}</tbody>
        </table>
       </div>
       {/* Mobile cards view (<640px) */}
       <div className="sm:hidden divide-y divide-border" data-testid="emp-mobile-list">
        {sortedUsers.map(u => (
          <div key={u.email} className="p-3 space-y-2" data-testid={`emp-card-${u.email}`}>
            <div className="flex items-start justify-between gap-2">
              <button
                className="text-left font-medium hover:text-primary"
                onClick={() => setShowHistory(u)}
                data-testid={`emp-name-mobile-${u.email}`}
              >
                {formatEmployeeName(u)}
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setForm({...EMPTY, ...u}); setOpen(u.email); }} data-testid={`edit-mobile-${u.email}`}><Pencil className="w-3.5 h-3.5"/></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setBehalfFor(u)} title="Predaj zahtjev u ime" data-testid={`behalf-mobile-${u.email}`}><Send className="w-3.5 h-3.5"/></Button>
                {isFullAdmin && u.email !== user.email && (
                  <Button size="icon" variant="ghost"
                    className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => { setDeleteFor({ email: u.email, full_name: u.full_name }); setDeleteConflict(null); }}
                    title="Obriši zaposlenika"
                    data-testid={`delete-mobile-${u.email}`}>
                    <Trash2 className="w-3.5 h-3.5"/>
                  </Button>
                )}
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground">{u.email}</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div><span className="text-muted-foreground">Pozicija:</span> {u.radno_mjesto || u.katedra || "—"}</div>
              <div><span className="text-muted-foreground">Uloga:</span> {u.custom_role_name || ROLE_LABEL[u.role] || u.role}</div>
              <div className="col-span-2"><span className="text-muted-foreground">Nadređeni:</span> {u.manager_email || "—"}</div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Fond / Iskorišt.:</span> {u.ukupni_fond_dana ?? DEFAULT_VACATION_DAYS} / <b>{u.iskoristeno_dana_effective ?? u.iskoristeno_dana ?? 0}</b>
                <span className="text-[10px] text-muted-foreground ml-1">({u.iskoristeno_dana_manual_override ? "početno + auto" : "auto"})</span>
              </div>
            </div>
          </div>
        ))}
        {sortedUsers.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">Nema rezultata.</div>
        )}
       </div>
      </CardContent></Card>

      <EmployeeEditDialog
        open={open}
        onClose={() => { setOpen(null); setForm(EMPTY); }}
        form={form}
        setForm={setForm}
        onSave={save}
        managerCandidates={managerCandidates}
        customRoles={customRoles}
        workSchedules={workSchedules}
        isFullAdmin={isFullAdmin}
      />

      <BehalfVacationDialog
        employee={behalfFor}
        onClose={() => setBehalfFor(null)}
        onSuccess={() => load()}
      />

      <EmployeeImportDialog
        open={importOpen}
        onClose={closeImportDialog}
        file={importFile}
        onFileChange={(f) => { setImportFile(f); setImportResult(null); setImportMode("idle"); }}
        result={importResult}
        busy={importBusy}
        mode={importMode}
        onPreview={doPreviewImport}
        onApply={doApplyImport}
        onReset={resetImport}
      />

      <EmployeeHistorySheet employee={showHistory} onClose={() => setShowHistory(null)} />

      <Dialog open={!!deleteFor} onOpenChange={v => { if (!v) { setDeleteFor(null); setDeleteConflict(null); }}}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5"/>
              {deleteConflict ? "Aktivni zahtjevi — potvrda" : "Brisanje zaposlenika"}
            </DialogTitle>
          </DialogHeader>
          {!deleteConflict ? (
            <div className="space-y-2 text-sm">
              <p>Sigurno želite obrisati zaposlenika <b>{deleteFor?.full_name}</b>?</p>
              <p className="text-xs text-muted-foreground">
                Ova radnja je trajna. Historijski zahtjevi sa statusom <i>Rješenje izrađeno</i> ili <i>Odbijeno</i> ostat će u sustavu kao zapis. Aktivni zahtjevi (koji čekaju suglasnost, sa suglasnošću, rješenje u izradi) blokirat će brisanje — moći ćete ih otkazati u sljedećem koraku.
              </p>
            </div>
          ) : (
            <div className="space-y-2 text-sm bg-red-50 border border-red-200 rounded p-3">
              <p className="font-medium text-red-800">{deleteConflict.message}</p>
              <p className="text-xs text-red-700">
                Klikom na <b>Obriši uz otkaz zahtjeva</b> svi aktivni zahtjevi ovog zaposlenika bit će <b>nepovratno otkazani i izbrisani</b>. Historijski zahtjevi (poslano, odbijeno) ostaju u sustavu.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteFor(null); setDeleteConflict(null); }} data-testid="delete-cancel">
              Odustani
            </Button>
            {!deleteConflict ? (
              <Button onClick={() => doDelete(false)} disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-confirm">
                {deleting ? "Brišem…" : "Obriši"}
              </Button>
            ) : (
              <Button onClick={() => doDelete(true)} disabled={deleting}
                className="bg-red-700 hover:bg-red-800 text-white" data-testid="delete-confirm-force">
                {deleting ? "Brišem…" : "Obriši uz otkaz zahtjeva"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
